/**
 * Formatting helpers. Units follow the assistant's conventions (distances in km, speeds in
 * km/h, fuel in L/100 km, durations as "1 h 25 min") and dates are shown in the company's
 * IANA timezone (`/api/me.company.timezone`, set once at bootstrap).
 */
import { getLocale, INTL_LOCALE, t } from './i18n';

const NBSP = ' ';
let displayZone = 'Europe/Brussels';

export function setDisplayTimeZone(tz: string | null | undefined): void {
  if (tz) displayZone = tz;
}

export function getDisplayTimeZone(): string {
  return displayZone;
}

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>();
function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>(key: string, make: () => T): T {
  const k = `${getLocale()}|${key}`;
  let f = cache.get(k) as T | undefined;
  if (!f) {
    f = make();
    cache.set(k, f);
  }
  return f;
}

const intl = () => INTL_LOCALE[getLocale()];

export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value == null || Number.isNaN(value)) return '—';
  return memo(`n${digits}`, () => new Intl.NumberFormat(intl(), { maximumFractionDigits: digits, minimumFractionDigits: 0 })).format(value);
}

function withUnit(value: number | null | undefined, digits: number, unit: string): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${formatNumber(value, digits)}${NBSP}${unit}`;
}

/** 12.3 km (one decimal below 100 km). */
export function formatKm(km: number | null | undefined): string {
  if (km == null) return '—';
  return withUnit(km, Math.abs(km) < 100 ? 1 : 0, 'km');
}

/** Metres as "850 m" or "12.3 km". */
export function formatMetres(m: number | null | undefined): string {
  if (m == null) return '—';
  return Math.abs(m) < 1000 ? withUnit(m, 0, 'm') : formatKm(m / 1000);
}

export function formatSpeed(kmh: number | null | undefined): string {
  return withUnit(kmh, 0, 'km/h');
}

export function formatFuel(litresPer100: number | null | undefined): string {
  return withUnit(litresPer100, 1, 'L/100 km');
}

export function formatLitres(litres: number | null | undefined): string {
  return withUnit(litres, 0, 'L');
}

export function formatTonnes(tonnes: number | null | undefined): string {
  return withUnit(tonnes, 1, 't');
}

/** Durations: "45 s", "25 min", "1 h 25 min", "2 d 3 h". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const s = Math.round(Math.abs(seconds));
  if (s < 60) return t('{seconds} s', { seconds: s });
  const minutes = Math.round(s / 60);
  if (minutes < 60) return t('{minutes} min', { minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return rest ? t('{hours} h {minutes} min', { hours, minutes: rest }) : t('{hours} h', { hours });
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return h ? t('{days} d {hours} h', { days, hours: h }) : t('{days} d', { days });
}

type DateStyle = 'date' | 'datetime' | 'time' | 'short' | 'weekday' | 'seconds';
const DATE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  date: { day: 'numeric', month: 'short', year: 'numeric' },
  datetime: { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  time: { hour: '2-digit', minute: '2-digit' },
  short: { day: 'numeric', month: 'short' },
  weekday: { weekday: 'long', day: 'numeric', month: 'long' },
  /** Device messages: to the second. */
  seconds: { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' },
};

export function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A date in the company timezone (or `tz`). */
export function formatDate(value: string | number | Date | null | undefined, style: DateStyle = 'date', tz?: string): string {
  const d = toDate(value);
  if (!d) return '—';
  const zone = tz ?? displayZone;
  return memo(`d${style}${zone}`, () => new Intl.DateTimeFormat(intl(), { ...DATE_OPTIONS[style], timeZone: zone })).format(d);
}

/** A calendar date without a time zone shift ("2026-09-01" stays the 1st). */
export function formatCalendarDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '—';
  return formatDate(`${isoDate.slice(0, 10)}T12:00:00Z`, 'date', 'UTC');
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86400],
  ['month', 30 * 86400],
  ['week', 7 * 86400],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

/** "5 min ago", "yesterday", "in 2 hr". */
export function formatRelative(value: string | number | Date | null | undefined, now: number = Date.now()): string {
  const d = toDate(value);
  if (!d) return '—';
  const diff = (d.getTime() - now) / 1000;
  const abs = Math.abs(diff);
  if (abs < 45) return t('just now');
  const rtf = memo('rel', () => new Intl.RelativeTimeFormat(intl(), { numeric: 'auto', style: 'short' }));
  for (const [unit, secs] of UNITS) {
    if (abs >= secs || unit === 'second') return rtf.format(Math.round(diff / secs), unit);
  }
  return rtf.format(0, 'second');
}

/** Initials for avatars: "Alex Admin" → "AA". */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}
